import { i18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import FormSection from '../../form/FormSection';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { SORT_ASC, SORT_DESC } from '../../../utilities/FormUtilities';
import {
    IP_RULES_FIELD,
    MAX_AUTO_INTERFACES,
    WAN_DEVICE_DESCRIPTION_FIELD,
    WAN_DEVICE_DUPLEX_FIELD,
    WAN_DEVICE_IF_TYPE_FIELD,
    WAN_DEVICE_IP_ADDRESS_FIELD,
    WAN_DEVICE_MANUAL_FIELD,
    WAN_DEVICE_NAME_FIELD,
    WAN_DEVICE_NORM_IP_ADDRESS_FIELD,
    WAN_INTERFACES_FIELD,
    WAN_META_ADDS_FIELD,
    WAN_META_FIELD,
    WAN_NAME_FIELD,
} from './SiteAdminConstants';
import { useAvailableWanLoader, useSelectedWanLoader } from './SiteAdminWanHelper';
import { ID_FIELD } from '../../form/FormConstants';
import ValidationError from '../../form/ValidationError';
import SiteAdminWanListSelectorField from './SiteAdminWanListSelectorField';

const addInterfaceButton = t`Add Interface`;
const emptyWanGridText = t`Click the Add Interface button above to manually add interfaces to this Site`;

const initialSort = [{ property: WAN_DEVICE_NAME_FIELD, direction: SORT_ASC }];

const associatedManuallyText = t`Manually`;
const associatedAutomaticallyText = t`Automatically`;

const wanColumns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Name`,
        sortable: true,
        mapping: WAN_NAME_FIELD,
        width: 200,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Device Name`,
        sortable: true,
        mapping: WAN_DEVICE_NAME_FIELD,
        width: 200,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Device IP`,
        sortable: true,
        mapping: WAN_DEVICE_IP_ADDRESS_FIELD,
        width: 200,
        sortProperty: WAN_DEVICE_NORM_IP_ADDRESS_FIELD,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Type`,
        sortable: true,
        mapping: WAN_DEVICE_IF_TYPE_FIELD,
        width: 150,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Description`,
        sortable: true,
        mapping: WAN_DEVICE_DESCRIPTION_FIELD,
        flex: 1,
    },
];

const selectedWanColumns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Added`,
        mapper: (row) => (row[WAN_DEVICE_MANUAL_FIELD] ? associatedManuallyText : associatedAutomaticallyText),
        getTooltip: (row) => (row[WAN_DEVICE_MANUAL_FIELD] ? associatedManuallyText : associatedAutomaticallyText),
        sortable: true,
        sortProperty: WAN_DEVICE_MANUAL_FIELD,
        width: 150,
    },
    ...wanColumns,
];

const baseDataSource = {
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [
            WAN_DEVICE_NAME_FIELD,
            WAN_DEVICE_IP_ADDRESS_FIELD,
            WAN_DEVICE_IF_TYPE_FIELD,
            WAN_NAME_FIELD,
            WAN_DEVICE_DESCRIPTION_FIELD,
            WAN_DEVICE_DUPLEX_FIELD,
        ],
    },
};

const selectedDataSource = {
    url: '/query/admin/interfaces',
    ...baseDataSource,
    initialSort: [{ property: WAN_DEVICE_MANUAL_FIELD, direction: SORT_DESC }],
};

const availableDataSource = {
    url: '/query/admin/interfaces/byDeviceRules',
    ...baseDataSource,
    initialSort: [{ property: WAN_NAME_FIELD, direction: SORT_ASC }],
};

const overloadedMessage = i18n._(
    `The global WAN interface rules matched more than ${MAX_AUTO_INTERFACES} interfaces for this site. WAN interfaces may be displayed/configured once the rules have been narrowed.`
);

export function SiteAdminWanInterfaces({
    formData,
    setWANSelectionIsDirty,
    overloaded,
    updateSelectionFunction,
    wanMeta,
}) {
    const { watch } = useFormContext();
    const rules = watch(IP_RULES_FIELD);
    const wanInterfaces = watch(WAN_INTERFACES_FIELD);

    if (_.isEmpty(rules)) {
        return (
            <FormSection
                title={<Trans>WAN Interfaces</Trans>}
                subText={
                    <Trans>
                        You must first define IP scopes for this site before you can select associated WAN interfaces
                    </Trans>
                }
            />
        );
    }

    const theSelectedDataSource = {
        ...selectedDataSource,
        params: {
            ...selectedDataSource.params,
            site: formData[ID_FIELD],
            [WAN_META_FIELD]: {
                [WAN_META_ADDS_FIELD]: wanMeta[WAN_META_ADDS_FIELD],
            },
        },
    };

    const theAvailableDataSource = {
        ...availableDataSource,
        params: availableDataSource.params,
        body: {
            rules,
        },
    };

    return (
        <FormSection title={<Trans>WAN Interfaces</Trans>} addSpacer>
            <ValidationError invalid={overloaded} message={overloadedMessage} />
            <SiteAdminWanListSelectorField
                gridColumns={wanColumns}
                selectedGridColumns={selectedWanColumns}
                sorters={initialSort}
                dbSelected={wanInterfaces}
                selectedDataSource={theSelectedDataSource}
                selectedLoaderFunction={useSelectedWanLoader}
                availableDataSource={theAvailableDataSource}
                availableLoaderFunction={useAvailableWanLoader}
                availableSelectAll={false}
                fieldName={WAN_INTERFACES_FIELD}
                addButtonText={addInterfaceButton}
                addButtonDisabled={!!overloaded}
                setSelectionIsDirty={setWANSelectionIsDirty}
                isMultiRemove={false}
                selectedEmptyGridText={emptyWanGridText}
                updateSelectionFunction={updateSelectionFunction}
            />
        </FormSection>
    );
}
